import { useEffect } from "react";
import { useLocation } from "react-router-dom";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

const INTERACTIVE_SELECTORS =
  'a, button, [role="button"], [role="link"], input[type="submit"], input[type="button"], [data-ga-click]';

function getClickTarget(element: EventTarget | null): Element | null {
  if (!(element instanceof Element)) return null;

  const el = element as Element;

  // Skip if explicitly opted out
  if (el.closest("[data-ga-ignore]")) return null;

  // Prefer custom tracking label on any element
  const withLabel = el.closest("[data-ga-label]");
  if (withLabel) return withLabel as Element;

  // Find closest interactive element
  const interactive = el.closest(INTERACTIVE_SELECTORS);
  return interactive;
}

function getElementDescriptor(el: Element): {
  element_type: string;
  element_label: string;
  link_url?: string;
} {
  const tagName = el.tagName.toLowerCase();
  const id = el.id;
  const dataLabel = el.getAttribute("data-ga-label");
  const ariaLabel = el.getAttribute("aria-label");
  const text =
    el.textContent?.trim().replace(/\s+/g, " ").slice(0, 100) || "";
  const href = (el as HTMLAnchorElement).href;

  let element_label = dataLabel || ariaLabel || id || text || tagName;
  if (element_label.length > 100) {
    element_label = element_label.slice(0, 97) + "...";
  }

  const result: {
    element_type: string;
    element_label: string;
    link_url?: string;
  } = {
    element_type: tagName,
    element_label: element_label || "unknown",
  };

  if (href) result.link_url = href;

  return result;
}

export function useClickTracking() {
  const location = useLocation();

  useEffect(() => {
    if (!window.gtag) return;

    const handleClick = (e: MouseEvent) => {
      const target = getClickTarget(e.target);
      if (!target) return;

      const { element_type, element_label, link_url } =
        getElementDescriptor(target);

      window.gtag!("event", "click", {
        event_category: "engagement",
        event_label: element_label,
        element_type,
        page_path: location.pathname + location.search,
        page_title: document.title,
        ...(link_url && { link_url }),
      });
    };

    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, [location.pathname, location.search]);
}
